import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
// Pricing hidden 2026-06-21 — re-enable when we charge again (restore this import + the Pricing link below)
// import { isPromoActive } from './PromoBanner';

// Pragmatic email regex — rejects malformed input (no @, no TLD, spaces, etc.)
// without trying to fully implement RFC 5322. Matches the same pattern used
// server-side in AuthService.register so the two stay consistent.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Strip the "Email support@creatorkhata.com within 30 days..." sentence from
// the ACCOUNT_DELETED message — once the user has a self-serve restore
// button right below, that mailto guidance is stale and confusing.
function stripSupportSentence(text) {
  return text.replace(/\s*Email support@creatorkhata\.com[^.]*\.\s*/i, ' ').trim();
}

const DRAWER_CSS = `
  @keyframes ckDrawerBackdrop { from { opacity: 0; } to { opacity: 1; } }
  @keyframes ckDrawerSlide    { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @keyframes ckDrawerSpin     { to { transform: rotate(360deg); } }

  .ck-drawer-root { position: fixed; inset: 0; z-index: 1500; font-family: 'Inter', sans-serif; }
  .ck-drawer-backdrop {
    position: absolute; inset: 0;
    background: rgba(10, 10, 12, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    animation: ckDrawerBackdrop 0.22s ease forwards;
  }
  .ck-drawer-panel {
    position: absolute; top: 0; right: 0; bottom: 0;
    width: 100%; max-width: 460px;
    background: #ffffff;
    box-shadow: -20px 0 60px rgba(10,10,12,0.18);
    display: flex; flex-direction: column;
    animation: ckDrawerSlide 0.32s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    overflow-y: auto;
  }

  .ck-drawer-head {
    padding: 1.5rem 1.75rem 0;
    display: flex; align-items: center; justify-content: space-between;
  }
  .ck-drawer-brand {
    display: inline-flex; align-items: center; gap: 0.55rem;
    text-decoration: none; color: #0a0a0c;
  }
  .ck-drawer-brand .mark {
    width: 32px; height: 32px; border-radius: 9px;
    background: linear-gradient(135deg,#6366f1,#ec4899);
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.85rem;
    box-shadow: 0 4px 14px rgba(99,102,241,0.3);
  }
  .ck-drawer-brand .name {
    font-family: 'Syne', sans-serif;
    font-weight: 800; font-size: 1.05rem;
    letter-spacing: -0.03em; color: #0a0a0c;
  }
  .ck-drawer-close {
    width: 36px; height: 36px; border-radius: 10px;
    background: #f4f4f5; border: none; cursor: pointer;
    color: #52525b; font-size: 1rem;
    display: inline-flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s;
  }
  .ck-drawer-close:hover { background: #e4e4e7; color: #0a0a0c; }

  .ck-drawer-body { padding: 1.75rem; flex: 1; }

  .ck-drawer-title {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(1.55rem, 2.5vw, 1.85rem);
    line-height: 1.1; letter-spacing: -0.03em;
    color: #0a0a0c;
    margin: 0.25rem 0 0.45rem;
  }
  .ck-drawer-sub {
    color: #71717a;
    font-size: 0.92rem;
    line-height: 1.55;
    margin: 0 0 1.5rem;
  }

  .ck-drawer-tabs {
    display: flex; background: #f4f4f5;
    border-radius: 12px; padding: 4px;
    margin-bottom: 1.5rem;
  }
  .ck-drawer-tab {
    flex: 1; border: none; cursor: pointer;
    padding: 0.65rem; border-radius: 9px;
    font-size: 0.88rem; font-weight: 700;
    color: #71717a; background: transparent;
    transition: background 0.18s, color 0.18s, box-shadow 0.18s;
    font-family: 'Inter', sans-serif;
  }
  .ck-drawer-tab.active {
    background: #ffffff;
    color: #0a0a0c;
    box-shadow: 0 1px 3px rgba(10,10,12,0.06), 0 1px 2px rgba(10,10,12,0.04);
  }

  .ck-drawer-google {
    display: flex; justify-content: center;
    margin-bottom: 1rem;
  }

  .ck-drawer-divider {
    display: flex; align-items: center; gap: 0.75rem;
    margin: 1rem 0;
    font-size: 0.72rem; color: #a1a1aa;
    font-weight: 600; letter-spacing: 0.06em;
  }
  .ck-drawer-divider::before,
  .ck-drawer-divider::after {
    content: ''; flex: 1; height: 1px; background: #e4e4e7;
  }

  .ck-drawer-form { display: flex; flex-direction: column; gap: 0.65rem; }
  .ck-drawer-input {
    width: 100%; padding: 0.85rem 1rem;
    border: 1.5px solid #e4e4e7; border-radius: 11px;
    background: #fafafa; color: #0a0a0c;
    font-size: 0.95rem; outline: none;
    transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
    font-family: 'Inter', sans-serif;
  }
  .ck-drawer-input::placeholder { color: #a1a1aa; }
  .ck-drawer-input:focus {
    border-color: #0a0a0c;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(10,10,12,0.06);
  }

  .ck-drawer-error {
    padding: 0.7rem 0.9rem;
    background: #fef2f2; border: 1px solid #fecaca;
    border-radius: 10px;
    font-size: 0.85rem; color: #b91c1c;
    display: flex; align-items: center; gap: 0.4rem;
  }

  .ck-drawer-submit {
    margin-top: 0.5rem;
    padding: 0.95rem; border: none; border-radius: 12px;
    background: #0a0a0c;
    color: white;
    font-size: 0.98rem; font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s, background 0.15s, box-shadow 0.15s;
    font-family: 'Inter', sans-serif;
    display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  }
  .ck-drawer-submit:hover:not(:disabled) {
    background: #18181b;
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(10,10,12,0.18);
  }
  .ck-drawer-submit:disabled {
    background: #d4d4d8; color: #a1a1aa; cursor: not-allowed;
  }

  .ck-drawer-spinner {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.35);
    border-top: 2px solid #ffffff;
    border-radius: 50%;
    animation: ckDrawerSpin 0.7s linear infinite;
  }

  .ck-drawer-legal {
    font-size: 0.75rem;
    color: #a1a1aa;
    text-align: center;
    line-height: 1.6;
    margin-top: 1.5rem;
  }
  .ck-drawer-legal a {
    color: #52525b;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .ck-drawer-foot {
    padding: 1.25rem 1.75rem 1.75rem;
    border-top: 1px solid #f4f4f5;
    display: flex; justify-content: center; gap: 1rem;
    flex-wrap: wrap;
  }
  .ck-drawer-foot a {
    color: #a1a1aa;
    text-decoration: none;
    font-size: 0.78rem;
    font-weight: 500;
  }
  .ck-drawer-foot a:hover { color: #52525b; }

  @media (max-width: 600px) {
    .ck-drawer-panel { max-width: none; }
    .ck-drawer-head, .ck-drawer-body { padding-left: 1.25rem; padding-right: 1.25rem; }
    .ck-drawer-foot { padding-left: 1.25rem; padding-right: 1.25rem; }
  }
`;

export default function AuthDrawer({ open, mode = 'login', onClose, onModeChange }) {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithEmail, register } = useAuth();

  const [name,      setName]      = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [pending,   setPending]   = useState(null); // { email } when register sent verification mail
  const [needsVerify, setNeedsVerify] = useState(null); // { email } when login blocked by unverified
  const [deletedAccount, setDeletedAccount] = useState(null); // { email } when login blocked by soft-delete
  const [oauthOnly, setOauthOnly]     = useState(false); // true when register collision is an OAuth-only user
  const [restoreSent, setRestoreSent] = useState(false);
  const [resendSent, setResendSent]   = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => { firstInputRef.current?.focus(); }, 320);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setError('');
    } else {
      // Drawer closed — wipe form state so a future open (e.g., after logout
      // or after the user dismissed mid-fill) starts clean. AuthDrawer is
      // mounted once at the app root, so without this the last-typed values
      // would leak across sessions.
      setName(''); setEmail(''); setPassword(''); setConfirmPw('');
      setError(''); setLoading(false);
      setPending(null); setNeedsVerify(null); setDeletedAccount(null);
      setOauthOnly(false);
      setResendSent(false); setRestoreSent(false);
    }
  }, [open, mode]);

  if (!open) return null;

  const isRegister = mode === 'register';

  const handleGoogle = async (cred) => {
    setLoading(true); setError(''); setNeedsVerify(null); setDeletedAccount(null);
    try {
      await loginWithGoogle(cred.credential);
      onClose();
      navigate('/dashboard');
    } catch (err) {
      const raw = err?.response?.data?.error || '';
      if (raw.startsWith('ACCOUNT_DELETED:')) {
        setDeletedAccount({ email: '' });
        setError(raw.substring('ACCOUNT_DELETED:'.length));
      } else {
        setError('Google sign-in failed. Try again.');
      }
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setNeedsVerify(null); setDeletedAccount(null); setOauthOnly(false);
    const cleanEmail = email.trim();
    if (!EMAIL_RE.test(cleanEmail)) {
      return setError('Please enter a valid email address (e.g., you@example.com)');
    }
    if (isRegister) {
      if (!name.trim())            return setError('Name is required');
      if (password !== confirmPw)  return setError('Passwords do not match');
      if (password.length < 6)     return setError('Password must be at least 6 characters');
    }
    setLoading(true);
    try {
      if (isRegister) {
        const result = await register(name.trim(), cleanEmail, password);
        // Hard gate — do NOT close drawer or navigate. Switch to pending screen.
        setPending({ email: result?.email || cleanEmail, message: result?.message });
      } else {
        await loginWithEmail(cleanEmail, password);
        onClose();
        navigate('/dashboard');
      }
    } catch (err) {
      const raw = err.response?.data?.error || 'Something went wrong.';
      if (raw.startsWith('EMAIL_NOT_VERIFIED:')) {
        setNeedsVerify({ email: cleanEmail });
        setError(raw.substring('EMAIL_NOT_VERIFIED:'.length));
      } else if (raw.startsWith('ACCOUNT_DELETED:')) {
        setDeletedAccount({ email: cleanEmail });
        setError(raw.substring('ACCOUNT_DELETED:'.length));
      } else if (raw.startsWith('EXISTS_OAUTH_ONLY:')) {
        setOauthOnly(true);
        setError(raw.substring('EXISTS_OAUTH_ONLY:'.length));
      } else {
        setError(raw);
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async (targetEmail) => {
    setResendSent(false);
    try {
      if (password) {
        // Option B: a brand-new signup has NO DB row yet, so the email-only
        // resend endpoint (findByEmail) can't re-bake the verification link.
        // Re-submit registration with the credentials still in form state —
        // the backend re-issues a fresh token and re-sends the email. (Backend
        // leaves an existing real name untouched when the resent name is blank,
        // so resending from the login screen can't wipe a legacy account's name.)
        await register((name || '').trim(), targetEmail, password);
      } else {
        // Fallback when the password isn't in state (e.g. the user refreshed
        // the pending screen): the legacy email-only endpoint still resends for
        // any pre-existing unverified row.
        await authAPI.resendVerificationByEmail(targetEmail);
      }
      setResendSent(true);
    } catch {
      // register() throws only when the email already belongs to a verified or
      // Google account — in which case the user can just sign in. Show success
      // either way to avoid leaking account state.
      setResendSent(true);
    }
  };

  const requestRestore = async (targetEmail) => {
    setRestoreSent(false);
    try {
      await authAPI.requestRestore(targetEmail);
    } catch {
      // Backend 200s regardless of whether the email exists (enumeration safety),
      // so any thrown error is a network/proxy issue. Treat as "sent" anyway —
      // the user shouldn't be blocked by infra hiccups, and the worst case is
      // they don't get an email and try again.
    }
    setRestoreSent(true);
  };

  const switchMode = next => {
    setError(''); setNeedsVerify(null); setDeletedAccount(null); setOauthOnly(false);
    onModeChange?.(next);
  };

  return (
    <>
      <style>{DRAWER_CSS}</style>
      <div className="ck-drawer-root" role="dialog" aria-modal="true" aria-label={isRegister ? 'Create account' : 'Sign in'}>
        <div className="ck-drawer-backdrop" onClick={onClose} />
        <div className="ck-drawer-panel">
          <div className="ck-drawer-head">
            <span className="ck-drawer-brand">
              <span className="mark">🎬</span>
              <span className="name">CreatorKhata</span>
            </span>
            <button className="ck-drawer-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          <div className="ck-drawer-body">
            {pending ? (
              <>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📨</div>
                <h2 className="ck-drawer-title">Check your inbox</h2>
                <p className="ck-drawer-sub" style={{ marginBottom: '1.25rem' }}>
                  We sent a verification link to <strong>{pending.email}</strong>.
                  Click the link in the email to activate your account and sign in.
                  The link expires in 1 hour.
                </p>
                <div style={{ background: '#f4f4f5', borderRadius: 12, padding: '0.9rem 1rem', fontSize: '0.85rem', color: '#52525b', marginBottom: '1.25rem', lineHeight: 1.55 }}>
                  Didn't get it within a minute? Check spam, then resend below.
                </div>
                {resendSent ? (
                  <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '0.7rem 0.9rem', fontSize: '0.88rem', color: '#047857', textAlign: 'center', marginBottom: '0.85rem' }}>
                    ✓ Verification link resent. Check {pending.email}.
                  </div>
                ) : (
                  <button type="button" onClick={() => resendVerification(pending.email)}
                    className="ck-drawer-submit"
                    style={{ marginTop: 0, marginBottom: '0.85rem' }}>
                    Resend verification link
                  </button>
                )}
                <button type="button"
                  onClick={() => { setPending(null); setResendSent(false); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#52525b', fontSize: '0.85rem', textDecoration: 'underline', display: 'block', margin: '0 auto', padding: 0, fontFamily: "'Inter', sans-serif" }}>
                  Use a different email
                </button>
              </>
            ) : (
            <>
            <h2 className="ck-drawer-title">
              {isRegister ? 'Create your account' : 'Welcome back'}
            </h2>
            <p className="ck-drawer-sub">
              {isRegister
                ? 'Start running your creator business in one app. No card needed.'
                : 'Sign in to your creator dashboard.'}
            </p>

            <div className="ck-drawer-tabs" role="tablist">
              <button
                role="tab" aria-selected={!isRegister}
                className={`ck-drawer-tab${!isRegister ? ' active' : ''}`}
                onClick={() => switchMode('login')}
              >
                Sign in
              </button>
              <button
                role="tab" aria-selected={isRegister}
                className={`ck-drawer-tab${isRegister ? ' active' : ''}`}
                onClick={() => switchMode('register')}
              >
                Create account
              </button>
            </div>

            <div className="ck-drawer-google">
              <GoogleLogin
                onSuccess={handleGoogle}
                onError={() => setError('Google sign-in failed.')}
                useOneTap={false}
                theme="outline"
                size="large"
                text={isRegister ? 'signup_with' : 'continue_with'}
                shape="rectangular"
                width="400"
              />
            </div>

            <div className="ck-drawer-divider">OR WITH EMAIL</div>

            <form className="ck-drawer-form" onSubmit={handleSubmit}>
              {isRegister && (
                <input
                  ref={firstInputRef}
                  className="ck-drawer-input"
                  type="text" placeholder="Your full name"
                  value={name} onChange={e => setName(e.target.value)} required
                />
              )}
              <input
                ref={isRegister ? null : firstInputRef}
                className="ck-drawer-input"
                type="email" placeholder="Email address"
                value={email} onChange={e => setEmail(e.target.value)} required
              />
              <input
                className="ck-drawer-input"
                type="password"
                placeholder={isRegister ? 'Create password (min 6 chars)' : 'Password'}
                value={password} onChange={e => setPassword(e.target.value)} required
              />
              {isRegister && (
                <input
                  className="ck-drawer-input"
                  type="password" placeholder="Confirm password"
                  value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required
                />
              )}

              {error && (
                <div className="ck-drawer-error" role="alert">
                  ⚠️ {deletedAccount ? stripSupportSentence(error) : error}
                  {needsVerify && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {resendSent ? (
                        <span style={{ color: '#047857', fontWeight: 700 }}>✓ Verification email resent to {needsVerify.email}.</span>
                      ) : (
                        <button type="button"
                          onClick={() => resendVerification(needsVerify.email)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontWeight: 700, textDecoration: 'underline', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>
                          Resend verification email
                        </button>
                      )}
                    </div>
                  )}
                  {deletedAccount && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {restoreSent ? (
                        <span style={{ color: '#047857', fontWeight: 700 }}>
                          ✓ If <strong>{deletedAccount.email}</strong> is a deleted account, a restore link is on its way. Check your inbox.
                        </span>
                      ) : (
                        <button type="button"
                          onClick={() => requestRestore(deletedAccount.email)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontWeight: 700, textDecoration: 'underline', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>
                          Email me a restore link →
                        </button>
                      )}
                    </div>
                  )}
                  {oauthOnly && (
                    <div style={{ marginTop: '0.5rem', color: '#b91c1c', fontWeight: 700 }}>
                      ↑ Click the Google button above to sign in.
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="ck-drawer-submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="ck-drawer-spinner" />
                    Please wait…
                  </>
                ) : isRegister ? 'Create account →' : 'Sign in →'}
              </button>
            </form>

            {!isRegister && (
              <div style={{ textAlign: 'center', marginTop: '0.85rem' }}>
                <Link to="/forgot-password" onClick={onClose} style={{
                  color: '#52525b', fontSize: '0.85rem', fontWeight: 500,
                  textDecoration: 'none', borderBottom: '1px solid #e4e4e7',
                  paddingBottom: 1,
                }}>
                  Forgot your password?
                </Link>
              </div>
            )}

            <div className="ck-drawer-legal">
              By continuing you agree to our{' '}
              <Link to="/terms" onClick={onClose}>Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" onClick={onClose}>Privacy Policy</Link>.
            </div>
            </>
            )}
          </div>

          <div className="ck-drawer-foot">
            <Link to="/contact" onClick={onClose}>Contact</Link>
            <Link to="/refund"  onClick={onClose}>Refund</Link>
            {/* Pricing hidden 2026-06-21: {!isPromoActive() && <Link to="/pricing" onClick={onClose}>Pricing</Link>} */}
          </div>
        </div>
      </div>
    </>
  );
}
